import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { catalogAdminKey } from './queries';
type Source = {
  supplier_hotel_id: number;
  supplier_hotel_code: string;
  display_name: string;
  country_code: string | null;
  locality: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  current_hotel_id: string | null;
};
export function DirectHotelMatch({
  hotel,
  account,
  onClose,
  onLinked,
}: {
  hotel: { hotel_id: string; display_name: string };
  account: string;
  onClose(): void;
  onLinked(): void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState(hotel.display_name);
  const [applied, setApplied] = useState(hotel.display_name);
  const [offset, setOffset] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Source | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const candidates = useQuery({
    queryKey: [...catalogAdminKey, 'direct-matches', account, hotel.hotel_id, applied, offset],
    queryFn: async () =>
      (
        await client.get<{ data: Source[]; meta: { count: number } }>('hotel-catalog-admin/direct-linking/candidates', {
          params: { hotel_id: hotel.hotel_id, connector_account_id: account, search: applied, offset },
        })
      ).data,
    enabled: applied.trim().length >= 2,
    retry: false,
  });
  useEffect(() => { setSelected(null); setConfirmed(false); }, [candidates.dataUpdatedAt]);
  const job = useQuery({
    queryKey: [...catalogAdminKey, 'direct-discovery', jobId],
    queryFn: async () =>
      (await client.get<{ data: { status: string } }>(`hotel-catalog-admin/import-jobs/${encodeURIComponent(jobId!)}`))
        .data.data,
    enabled: !!jobId,
    refetchInterval: (data) => (data && ['completed', 'terminal_failed'].includes(data.status) ? false : 3000),
    onSuccess: (data) => {
      if (data.status === 'completed') candidates.refetch();
    },
    retry: false,
  });
  const discovery = useMutation({
    mutationFn: async () =>
      (
        await client.post<{ data: { job_id: string } }>('hotel-catalog-admin/direct-linking/discovery', {
          hotel_id: hotel.hotel_id,
          connector_account_id: account,
        })
      ).data.data,
    onSuccess: (data) => setJobId(data.job_id),
  });
  const link = useMutation({
    mutationFn: async () => {
      if (!selected || !confirmed || (selected.current_hotel_id && selected.current_hotel_id !== hotel.hotel_id))
        throw new Error('selection');
      await client.post('hotel-catalog-admin/supplier-hotels/link-or-create', {
        connector_account_id: account,
        supplier_hotel_id: selected.supplier_hotel_id,
        supplier_hotel_code: selected.supplier_hotel_code,
        hotel_id: hotel.hotel_id,
        reason: 'Operator reviewed direct supplier identity against existing canonical hotel',
      });
    },
    onSuccess: onLinked,
  });
  const busy = discovery.isLoading || link.isLoading;
  const importing = jobId !== null && (!job.data || !['completed', 'terminal_failed'].includes(job.data.status));
  return (
    <Dialog
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {t('hotelDirectMatch.title')} · {hotel.display_name}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">{t('hotelDirectMatch.help')}</Alert>
          <Button href={`/dashboard/hotel-catalog-admin/hotels/${encodeURIComponent(hotel.hotel_id)}`} target="_blank" rel="noopener noreferrer">{t('hotelDirectLink.review')} · {hotel.display_name}</Button>
          <Button disabled={busy || importing} onClick={() => { setSelected(null); setConfirmed(false); discovery.mutate(); }}>
            {t('hotelDirectMatch.importCountry')}
          </Button>
          {importing && <Typography>{t('hotelDirectMatch.importing')}</Typography>}
          {job.data?.status === 'completed' && <Alert severity="success">{t('hotelDirectMatch.imported')}</Alert>}
          {(discovery.isError ||
            candidates.isError ||
            job.isError ||
            link.isError ||
            job.data?.status === 'terminal_failed') && (
            <Alert severity="error">{t('hotelCatalogAdmin.loadError')}</Alert>
          )}
          <TextField
            label={t('hotelDirectMatch.search')}
            value={search}
            disabled={busy}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ maxLength: 128 }}
          />
          <Button
            disabled={busy || search.trim().length < 2}
            onClick={() => {
              setApplied(search.trim());
              setOffset(0);
              setSelected(null);
              setConfirmed(false);
              candidates.refetch();
            }}
          >
            {t('hotelDirectMatch.find')}
          </Button>
          {!candidates.isFetching && candidates.data?.data.length === 0 && (
            <Typography>{t('hotelDirectMatch.empty')}</Typography>
          )}
          {candidates.data?.data.map((source) => (
            <Stack key={source.supplier_hotel_id} spacing={1}>
              <Typography>
                {source.display_name} · {source.supplier_hotel_code} · {source.country_code} · {source.locality}
              </Typography>
              <Typography>
                {source.address}{' '}
                {source.latitude !== null && source.longitude !== null && `(${source.latitude}, ${source.longitude})`}
              </Typography>
              <Button
                disabled={busy || (!!source.current_hotel_id && source.current_hotel_id !== hotel.hotel_id)}
                variant={selected?.supplier_hotel_id === source.supplier_hotel_id ? 'contained' : 'outlined'}
                onClick={() => {
                  setSelected(source);
                  setConfirmed(false);
                }}
              >
                {t(
                  source.current_hotel_id && source.current_hotel_id !== hotel.hotel_id
                    ? 'hotelDirectMatch.conflict'
                    : 'hotelDirectMatch.select',
                )}
              </Button>
            </Stack>
          ))}
          <Stack direction="row" spacing={1}>
            <Button
              disabled={busy || candidates.isFetching || offset === 0}
              onClick={() => {
                setOffset((v) => Math.max(0, v - 25));
                setSelected(null);
                setConfirmed(false);
              }}
            >
              {t('hotelDestinationOrder.previousMatches')}
            </Button>
            <Button
              disabled={busy || candidates.isFetching || candidates.data?.meta.count !== 25}
              onClick={() => {
                setOffset((v) => v + 25);
                setSelected(null);
                setConfirmed(false);
              }}
            >
              {t('hotelDestinationOrder.nextMatches')}
            </Button>
          </Stack>
          {selected && (
            <FormControlLabel
              control={
                <Checkbox checked={confirmed} disabled={busy} onChange={(e) => setConfirmed(e.target.checked)} />
              }
              label={t('hotelDirectMatch.confirm', {
                hotel: hotel.display_name,
                source: selected.display_name,
                code: selected.supplier_hotel_code,
              })}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('hotelDirectMatch.close')}
        </Button>
        <Button disabled={busy || importing || candidates.isFetching || !selected || !confirmed} onClick={() => link.mutate()}>
          {t('hotelDirectMatch.link')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
